import { useForm } from 'react-hook-form'

import { useIs3dPrintModel } from 'Pods/ItemPage/TopSection/hooks/useTopSectionProps'

export default function useUseCaseForm({ isProcessingMedia }) {
  const {
    reset,
    watch,
    control,
    setValue,
    getValues,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      pendingMedia: [],
      printerType: null,
      material: null,
      printSuccess: null,
      technology: null,
      printTime: null,
      usedSupport: false,
      description: '',
      slicerFile: null,
    },
  })
  const is3dPrintModel = useIs3dPrintModel()

  const pendingMedia = watch('pendingMedia')
  const slicerFile = watch('slicerFile')

  const isFormInvalid = isProcessingMedia || (pendingMedia.length === 0 && !isDirty)

  const appendIfExists = (formData, key, value) => {
    if (!value) return
    const finalValue = value.value ?? value
    if (finalValue) {
      formData.append(`item_use_case[${key}]`, finalValue)
    }
  }

  // Build FormData from current form values
  const buildFormData = () => {
    const formData = new FormData()
    const values = getValues()

    if (is3dPrintModel) {
      appendIfExists(formData, 'technology', values.technology)
      appendIfExists(formData, 'material', values.material)
      appendIfExists(formData, 'print_success', values.printSuccess)
      appendIfExists(formData, 'print_time', values.printTime)

      if (values.printerType) {
        const printerTypeId = values.printerType.printerTypeId ?? values.printerType.value
        if (printerTypeId) {
          formData.append('item_use_case[printer_type_id]', printerTypeId)
        }
      }

      if (values.usedSupport !== undefined && values.usedSupport !== null) {
        formData.append('item_use_case[used_support]', values.usedSupport.toString())
      }

      if (values.description?.trim()) {
        formData.append('item_use_case[description]', values.description.trim())
      }

      if (values.slicerFile) {
        formData.append('item_use_case[slicer_file]', values.slicerFile)
      }
    }

    values.pendingMedia.forEach((mediaItem, index) => {
      formData.append(
        `item_use_case[use_case_embeds_attributes][${index}][position]`,
        mediaItem.position || (index + 1)
      )
      formData.append(`item_use_case[use_case_embeds_attributes][${index}][external_id]`, mediaItem.externalId || '')
      formData.append(`item_use_case[use_case_embeds_attributes][${index}][source]`, mediaItem.source)
      if (mediaItem.file) {
        formData.append(`item_use_case[use_case_embeds_attributes][${index}][embed_url]`, mediaItem.file)
      }
    })

    return formData
  }

  const getFormValues = () => {
    const values = getValues()
    return { ...values, pendingMedia: [...values.pendingMedia] }
  }

  const restoreFormValues = (values) => {
    reset(values, {
      keepDefaultValues: false,
      keepDirty: true,
    })
  }

  const updatePendingMedia = (mediaOrUpdater) => {
    if (typeof mediaOrUpdater === 'function') {
      const currentMedia = getValues('pendingMedia')
      const newMedia = mediaOrUpdater(currentMedia)
      setValue('pendingMedia', newMedia, { shouldDirty: newMedia.length > 0 })
    } else {
      setValue('pendingMedia', mediaOrUpdater, { shouldDirty: mediaOrUpdater.length > 0 })
    }
  }

  const handleSlicerFileSelect = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setValue('slicerFile', acceptedFiles[0], { shouldDirty: true })
    }
  }

  const handleSlicerFileRemove = () => {
    setValue('slicerFile', null, { shouldDirty: true })
  }

  const resetForm = () => {
    reset()
  }

  const hasUnsavedChanges = isDirty || pendingMedia.length > 0

  const canSubmit = !isProcessingMedia && pendingMedia.length > 0

  return {
    control,
    pendingMedia,
    slicerFile,
    isFormInvalid,
    buildFormData,
    getFormValues,
    restoreFormValues,
    updatePendingMedia,
    handleSlicerFileSelect,
    handleSlicerFileRemove,
    resetForm,
    hasUnsavedChanges,
    canSubmit,
  }
}
